<template>
  <v-card flat>
    <v-card-text :class="getContainerClass">
      <v-row dense>
        <v-col cols="12" v-if="!hideHeader">
          <TableActions
          :actions="actions"
          :showSearch="showSearch"
          :showDense="showDense"
          :htmlIdentifier="htmlIdentifier"
          @search="searchChanged"
          @dense-toggled="denseToggled"
          @table-action="emitTableAction"
        >
          <template #prepend-search v-if="showSearch">
            <slot name="prepend-search"/>
          </template>
          <template #search-replacement>
            <slot name="search-replacement"></slot>
          </template>
          <template #custom-controls>
            <slot name="custom-controls"></slot>
          </template>
        </TableActions>
        </v-col>
        <v-col cols="12">
          <v-row dense>
            <v-col cols="12" class="py-0">
              <slot name="filters"></slot>
            </v-col>
            <v-col cols="12" class="py-0">
              <slot name="selection-controls"></slot>
            </v-col>
            <v-col cols="12" id="data-table-parent-col"> <!-- This ID is to help CSS selectors -->
              <v-data-table
              fixed-header
              :height="tableHeight"
              :id="tableElementId"
              :class="tableClass"
              ref="dataTable"
              :items="displayItems"
              :headers="headers"
              :search="serverSide ? undefined : (enableExternalSearch ? externalSearch : search)"
              :dense="dense"
              :group-by="groupBy"
              :item-key="tableKey"
              :expanded="expanded"
              :show-select="showSelect"
              v-model="propSelected"
              :show-expand="showExpand"
              :sort-by="serverSide ? undefined : defaultSort"
              :sort-desc="serverSide ? undefined : sortDesc"
              :footer-props="footerProps"
              :items-per-page="getItemsPerPage"
              :loading="loading"
              :hide-default-footer="hideTableFooter"
              :server-items-length="serverSide ? serverItemsLength : undefined"
              :no-data-text="$t('noItemsAvailable')"
              :item-class="itemClass"
              @current-items="setCurrentItems"
              @click:row="rowClicked"
              @update:items-per-page="itemsPerPageUpdated"
              @update:options="onOptionsUpdated"
              checkbox-color="success"
              :style="tableStyle"
            >
              <template
                v-for="slot in customCells"
                v-slot:[`item.${slot.value}`]="{ item, index }">
                <span v-if="slot.dateFormat" :key="slot.value">
                  {{getDate(item[slot.value], slot.dateFormat)}}
                </span>
                <slot v-else :name="`${slot.slotName}`" :item="item" :index="index"></slot>
              </template>

              <template
                v-for="slot in customHeaders"
                #[`header.${slot.value}`]="{ slotProp }">
                <slot :name="`${slot.slotName}`" v-bind="slotProp"/>
              </template>

              <template #header.data-table-select="{ slotProp }">
                <slot name="header.data-table-select" v-bind="slotProp"/>
                <Icon
                v-if="!hideDefaultSelectAll"
                top
                :icon="iconForSelectAllBox"
                :iconColor="allSelected ? 'success' : 'rgba(0,0,0,0.3)'"
                :tooltipText="getSelectAllTooltipText"
                tooltipColor="secondary"
                :small="false"
                @icon-clicked="selectAllClicked"
                :disabled="selectAllDisabled"
                :style="selectAllStyle"
                />
              </template>

              <template #group.header="slotProp" v-if="groupBy">
                <td
                  :colspan="headers.length + 1"
                  :style="{ backgroundColor: 'white',
                    borderLeft: `solid 4px ${theme.primary}`,
                    boxShadow: 'inset 0 6px 2px -4px rgba(0, 0, 0, 0.1)' }"
                  :class="abbreviateHeaders || smallHeaders ? 'py-2 px-4' : 'pa-4'"
                  :id="$vuetify.breakpoint.xs ? 'group-header-xs' : 'group-header'"
                >
                  <slot name="group.header" v-bind="slotProp">
                    <span :class="!smallHeaders ? 'title' : 'body-2 font-weight-bold'">
                      {{ slotProp.group?.length > 0 ? slotProp.group : groupByUnspecifiedText }}
                    </span>
                  </slot>
                </td>
              </template>

              <template #group.summary="slotProp" v-if="groupBy">
                <slot name="group.summary" v-bind="slotProp">
                  <slot name="group.summary.prepend" v-bind="slotProp"/>
                  <v-col v-if="$vuetify.breakpoint.xs" class="pa-0 group-totals-row-mobile">
                    <tr v-for="(total, i) in getFormattedTotals(slotProp.items)" :key="`table-total-${i}`" :class="`${total.classes} font-weight-bold v-data-table__mobile-table-row`">
                      <td class="v-data-table__mobile-row">
                        <div class="v-data-table__mobile-row__header pl-4" style="font-size:.75rem"> {{ $t(total.name) }} </div>
                        <div class="v-data-table__mobile-row__cell pr-4" style="font-size:.75rem"> {{ total.value }} </div>
                      </td>
                    </tr>
                  </v-col>
                  <td v-for="(total, i) in getFormattedTotals(slotProp.items)" :key="`table-total-${i}`" :class="`${total.classes} font-weight-bold`">
                    <div v-if="!$vuetify.breakpoint.xs">
                      <slot :name="`group-total.${total.name}`" v-bind="{ total, group: slotProp }">
                        {{total.value}}
                      </slot>
                    </div>
                  </td>
                  <slot name="group.summary.append" v-bind="slotProp"/>
                </slot>
              </template>

              <template v-for="h in headers.filter(h => h.tooltip)" v-slot:[`header.${h.value}`]>
                <v-tooltip :key="h.value" color="primary" top >
                  <template v-slot:activator="{ on }">
                    <span :style="abbreviateHeaders ? 'font-size: .8rem !important;' : ''" v-on="on">{{ abbreviateHeaders ? h.textAbbr || h.text : h.text }}</span>
                  </template>
                  <span class="subtitle-1 white--text" style="white-space: pre-line">{{h.tooltip}}</span>
                </v-tooltip>
              </template>

              <template v-for="header in formattedHeaders" #[`item.${header.value}`]="{ item }">
                <span :key="`item-${header.value}`">
                {{header.format ? header.format(item[header.value]) : item[header.value]}}
                </span>
              </template>

              <template #body.append="{ groupedItems }">
                <tr v-if="!$vuetify.breakpoint.xs && groupBy && groupedItems?.length > 1">
                  <div class="py-4" style="background-color: white;"/>
                </tr>
                <tr v-if="!$vuetify.breakpoint.xs && groupedItems?.length !== 1 && showTotals" class="font-weight-bold" style="border: solid thin;">
                  <td v-for="(total, i) in formattedTotals" :key="`table-total-${i}`" :class="total.classes">
                    <slot :name="`total.${total.name}`" v-bind="total">
                      {{total.value}}
                    </slot>
                  </td>
                </tr>
                <span
                  v-if="$vuetify.breakpoint.xs && groupedItems?.length !== 1 && showTotals"
                  class="text-subtitle-2 ml-4 mt-4 mb-2 font-weight-black"
                >
                  {{ $t('totals') }}
                </span>
                <div v-if="$vuetify.breakpoint.xs && groupedItems?.length !== 1 && showTotals">
                  <tr v-for="(total, i) in formattedTotals" :key="`table-total-${i}`" :class="`${total.classes} font-weight-bold v-data-table__mobile-table-row`">
                    <td class="v-data-table__mobile-row">
                      <div class="v-data-table__mobile-row__header pl-4" style="font-size:.75rem"> {{ $t(total.name) }} </div>
                      <div class="v-data-table__mobile-row__cell pr-4" style="font-size:.75rem"> {{ total.value }} </div>
                    </td>
                  </tr>
                </div>
                <slot name="body.append"></slot>
              </template>

              <template #footer>
                <slot name="footer"></slot>
              </template>

              <template #footer.prepend>
                <slot name="footer.prepend"></slot>
              </template>

              <template #item.data-table-expand="{ item }">
                <v-icon v-if="shouldShowExpandIcon(item)" @click="expandItem(item)">{{ expandIcon }}</v-icon>
              </template>

              <template #expanded-item="{item}">
                <td :colspan="headers.length" style="background-color: #eee;">
                  <slot name="expanded-item" :item="item"></slot>
                </td>
              </template>
            </v-data-table>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import {
  tickTock, dataTableItemsPerPage, dataTableDefaultItemsPerPage, getDataTableDefaultItemsPerPage, tableHeight,
  dataTableMinItemsPerPage, dataTableItemsPerPageIncludeAll, tableHeightActual, uniqueDialogId
} from '@/utils/componentHelpers'
import { sumsFor } from '@/utils/base'
import { mapGetters } from 'vuex'
import { theme } from '@/plugins/theme'

export default {
  name: 'DataTable',

  components: {
    TableActions: () => import('@/components/core/table/TableActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    items: {
      type: Array,
      required: true
    },
    customCells: {
      type: Array,
      required: false
    },
    headers: {
      type: Array,
      required: true
    },
    sumHeaders: {
      type: Array,
      required: false,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    expandable: {
      type: Boolean,
      default: false
    },
    actions: {
      type: Array,
      required: false
    },
    showExpand: {
      type: Boolean,
      default: false
    },
    showDense: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    enableExternalSearch: {
      type: Boolean,
      default: false
    },
    externalSearch: {
      type: String,
      default: ''
    },
    expandIcon: {
      type: String,
      default: 'mdi-chevron-down'
    },
    hideHeader: {
      type: Boolean,
      default: false
    },
    tableKey: {
      type: String,
      required: false,
      default: 'default-key'
    },
    propDense: {
      type: Boolean,
      default: true
    },
    sortDesc: {
      type: Boolean,
      default: false
    },
    defaultSort: {
      type: String,
      default: ''
    },
    sumValues: {
      type: Array,
      default: () => []
    },
    activityTemplate: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Array,
      default: () => []
    },
    showSelect: {
      type: Boolean,
      default: false
    },
    showTotals: {
      type: Boolean,
      required: false,
      default: false
    },
    groupBy: {
      type: String,
      required: false,
      default: undefined
    },
    groupByUnspecifiedText: {
      type: String,
      required: false,
      default: ''
    },
    abbreviateHeaders: {
      type: Boolean,
      required: false,
      default: false
    },
    smallHeaders: {
      type: Boolean,
      required: false,
      default: false
    },
    keepSelectionWhenItemsChange: {
      type: Boolean,
      required: false,
      default: false
    },
    containerClass: {
      type: String,
      required: false,
      default: undefined
    },
    resize: {
      type: Boolean,
      default: false
    },
    expandOnCreation: {
      type: Boolean,
      default: false
    },
    htmlIdentifier: {
      type: String,
      required: false,
      default: undefined
    },
    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({
        offset: 0,
        auto: true
      })
    },
    allowViewAll: {
      type: Boolean,
      required: false,
      default: false
    },
    allSelected: {
      type: Boolean,
      required: false,
      default: false
    },
    propSelectAllTooltipText: {
      type: String,
      required: false,
      default: undefined
    },
    hideDefaultSelectAll: {
      type: Boolean,
      required: false,
      default: false
    },
    tableElementId: {
      type: String,
      required: false,
      default: () => uniqueDialogId('angel-data-table')
    },
    disablePagination: {
      type: Boolean,
      default: false
    },
    showPointerCursor: {
      type: Boolean,
      default: false
    },
    propItemsPerPage: {
      type: Number,
      required: false,
      default: undefined
    },
    itemsPerPageOptions: {
      type: Array,
      default: undefined
    },
    itemClass: {
      type: [String, Array, Object, Function],
      required: false,
      default: () => 'angel-table-row'
    },
    expandOnRowClick: {
      type: Boolean,
      default: false
    },
    itemCanExpandFunction: {
      type: Function,
      required: false,
      default: () => true
    },
    customHeaders: {
      type: Array,
      required: false,
      default: () => []
    },
    propExpanded: {
      type: Array,
      required: false,
      default: () => []
    },
    serverSide: {
      type: Boolean,
      default: false
    },
    serverItemsLength: {
      type: Number,
      default: undefined
    }
  },

  watch: {
    items (_) {
      if (!this.keepSelectionWhenItemsChange) this.propSelected = []
      if (this.expandOnCreation) {
        const firstExpandable = this.items.find(i => i.isExpandable)
        this.expanded = firstExpandable ? [firstExpandable] : []
      }
      this.assignTableHeightWithDelay()
    },

    currentItems () {
      this.$emit('filtered-items', this.$refs.dataTable.$children[0].filteredItems)
      this.assignTableHeightWithDelay()
    },

    allSelected (val) {
      if (val) {
        this.selectAllTooltipText = this.$t('deselectAll')
      } else {
        this.selectAllTooltipText = this.$t('selectAll')
      }
    },

    '$vuetify.breakpoint.width' () { this.assignTableHeight() },
    '$vuetify.breakpoint.height' () { this.assignTableHeight() },
    'drawerIsMini' () { this.assignTableHeightWithDelay(250) },

    topDialogState: {
      handler () {
        this.assignTableHeightWithDelay(100)
      },
      deep: true
    },

    tableHeightCalculationData: {
      handler () {
        this.assignTableHeightWithDelay()
      },
      deep: true
    },

    expanded (val) {
      this.$emit('expanded', val)
    },

    propExpanded (val) {
      this.expanded = val
    }
  },

  data: () => ({
    theme,
    search: '',
    dense: true,
    expanded: [],
    currentItems: undefined,
    currentItemsPerPage: dataTableDefaultItemsPerPage,
    selectAllTooltipText: null,
    tableHeight: 'auto',
    serverSideSort: { by: '', desc: false }
  }),

  created () {
    this.dense = this.propDense
    if (this.showSelect) this.selectAllTooltipText = this.$t('selectAll')
    this.expanded = this.propExpanded
  },

  computed: {
    ...mapGetters('app', ['drawerIsMini']),
    ...mapGetters('dialog', ['topDialogState']),

    customValues () {
      return (this.customCells !== undefined)
        ? new Set(this.customCells.map(c => c.value))
        : new Set()
    },

    formattedHeaders () {
      return this.headers.filter(h => h.format !== undefined && !this.customValues.has(h.value))
    },

    formattedTotals () {
      return this.getFormattedTotals(this.items)
    },

    propSelected: {
      get () { return this.selected },
      set (value) { this.$emit('update:selected', value) }
    },

    defaultItemsPerPage () {
      return dataTableDefaultItemsPerPage
    },

    getContainerClass () {
      let containerClass = this.containerClass ?? ''
      if (this.htmlIdentifier) containerClass = containerClass.concat(` ${this.htmlIdentifier}-table-container`)
      return containerClass
    },

    hideTableFooter () {
      if (this.serverSide) return false
      return this.disablePagination || this.items.length <= dataTableMinItemsPerPage
    },

    footerProps () {
      if (this.serverSide) {
        return {
          'items-per-page-options': dataTableItemsPerPage,
          'disable-pagination': this.loading,
          'disable-items-per-page': this.loading
        }
      }
      return {
        'items-per-page-options': this.itemsPerPageOptions ?? ((this.allowViewAll || this.disablePagination) ? dataTableItemsPerPageIncludeAll : dataTableItemsPerPage)
      }
    },

    displayItems () {
      if (!this.serverSide) return this.items
      const term = this.search.toLowerCase()
      let result = term
        ? this.items.filter(item =>
          this.headers.some(h => h.value && String(item[h.value] ?? '').toLowerCase().includes(term))
        )
        : this.items
      if (this.serverSideSort.by) {
        const key = this.serverSideSort.by
        const desc = this.serverSideSort.desc
        result = [...result].sort((a, b) => {
          const av = a[key] ?? ''
          const bv = b[key] ?? ''
          const cmp = av < bv ? -1 : av > bv ? 1 : 0
          return desc ? -cmp : cmp
        })
      }
      return result
    },

    iconForSelectAllBox () {
      return this.allSelected ? 'mdi-checkbox-multiple-marked' : this.selected.length > 0 ? 'mdi-minus-box-multiple' : 'mdi-checkbox-multiple-blank-outline'
    },

    getSelectAllTooltipText () {
      return this.propSelectAllTooltipText ?? this.selectAllTooltipText
    },

    selectAllDisabled () {
      return (this.showSearch && this.search) || (this.enableExternalSearch && this.externalSearch)
    },

    selectAllStyle () {
      return this.selectAllDisabled ? 'pointer-events: none; opacity: 0.25;' : ''
    },

    getItemsPerPage () {
      return this.disablePagination ? -1 : this.propItemsPerPage ? this.propItemsPerPage : getDataTableDefaultItemsPerPage(this.$vuetify.breakpoint)
    },

    tableClass () {
      let result = ''
      if (this.abbreviateHeaders) {
        result += 'small-headers-table'
      }
      if (this.smallHeaders) {
        result += ' small-headers'
      }
      return result
    },

    tableStyle () {
      return this.showPointerCursor ? 'cursor: pointer;' : ''
    }
  },

  methods: {
    getDataTableDefaultItemsPerPage,
    tickTock,
    utcToLocalDate,
    searchChanged (val) {
      this.search = val
      this.$emit('search-changed', val)
    },

    expandItem (item) {
      if (this.itemCanExpandFunction && !this.itemCanExpandFunction(item)) {
        return
      }
      if (this.expanded.length === 0) {
        this.expanded = [item]
      } else {
        if (this.expanded[0][this.tableKey] === item[this.tableKey]) {
          this.expanded = []
        } else {
          this.expanded = [item]
        }
      }
    },

    denseToggled (val) {
      this.dense = val
    },
    emitTableAction (val) {
      this.$emit(val)
    },

    getDate (date, dateFormat) {
      if (date === '' || !date) {
        return this.$t('notAvailable')
      } else {
        return utcToLocalDate(date, dateFormat)
      }
    },

    shouldShowExpandIcon (item) {
      if (item.activityDetails) {
        return item.isExpandable && this.expandable
      } else {
        return true
      }
    },

    setCurrentItems (val) {
      this.currentItems = val
    },

    getFormattedTotals (items) {
      if (!this.showTotals) return []

      const headers = (this.groupBy)
        ? this.headers.filter(h => h.value !== this.groupBy)
        : this.headers

      const sums = sumsFor(items, this.sumHeaders ?? headers.filter(h => h.total).map(h => h.value))

      const alignmentClassFor = hh => {
        switch (hh.align) {
          case 'right':
          case 'end':
            return 'text-right'
          case 'center':
          case 'middle':
            return 'text-center'
          default:
            return 'text-left'
        }
      }

      return headers.map(h => {
        const totalValue = (h.total)
          ? h.total({ items, sums, total: sums[h.value], count: items.length })
          : undefined

        return {
          name: h.value,
          value: totalValue !== undefined ? h.format !== undefined ? h.format(totalValue) : totalValue : '-',
          classes: alignmentClassFor(h)
        }
      })
    },

    rowClicked (data, related, e) {
      if (this.expandOnRowClick) {
        this.expandItem(data)
        this.assignTableHeightWithDelay()
      }
      this.$emit('click:row', { data, related, e })
    },

    onOptionsUpdated (options) {
      if (this.serverSide) {
        this.serverSideSort = { by: options.sortBy[0] ?? '', desc: options.sortDesc[0] ?? false }
      }
      this.$emit('update:options', options)
    },

    itemsPerPageUpdated (val) {
      this.currentItemsPerPage = val
      this.$emit('update:items-per-page', val)
    },

    selectAllClicked () {
      this.$emit('select-all-clicked')
    },

    assignTableHeightWithDelay (delay = 50) {
      setTimeout(() => {
        this.assignTableHeight()
      }, delay)
    },

    assignTableHeight () {
      if (this.tableHeightCalculationData.auto) {
        this.tableHeight = 'auto'
        return
      }

      const footerVisible = !this.hideTableFooter

      const actualTableHeight = tableHeightActual(this.tableHeightCalculationData.offset, this.tableElementId, footerVisible, this.tableHeightCalculationData.absoluteMinimum)
      if (actualTableHeight) {
        this.tableHeight = actualTableHeight
        return
      }

      const currIpp = this.currentItemsPerPage > 0 ? this.currentItemsPerPage : Number.MAX_SAFE_INTEGER
      const dataLength = Math.min(this.items.length, currIpp)
      this.tableHeight = tableHeight({
        dataLength,
        tableElementId: this.tableElementId,
        offset: this.tableHeightCalculationData.offset,
        headersLength: this.headers.length,
        footerVisible,
        absoluteMinimum: this.tableHeightCalculationData.absoluteMinimum ?? 256,
        absoluteMaximum: this.tableHeightCalculationData.absoluteMaximum ?? Number.MAX_SAFE_INTEGER
      })
    }
  }
}
</script>
<style>
#data-table-parent-col>.small-headers-table>.v-data-table__wrapper>table>tbody>tr>td {
  font-size: .75rem;
}
#data-table-parent-col>.small-headers-table #group-header>span.title {
  font-size: .85rem !important;
}
#data-table-parent-col>.small-headers-table #group-header-xs>span.title {
  font-size: .9rem !important;
  font-weight: 600;
}
</style>
